import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from '../../config/storageKeys';
import { landingHandoffAPI } from '../edu-landing/api/landingHandoffAPI';
import { APIError } from '../../lib/api/APIService';
import { clearAllPendingHandoffState } from '../edu-landing/handoffContinuation';

const EMPTY_STATE_COPY = 'Pick a decision below and see how IVORI reasons through it.';
const HINT_COPY = 'Four decisions, reasoned through below.';

// Deliberate pause before showing the answer — it already arrives from the
// API instantly, which reads as implausible for something meant to look
// generated. Matches edu's own TYPING_PAUSE_MS rationale exactly.
const TYPING_PAUSE_MS = 900;

// Terminal outcome: the token itself is genuinely dead (never existed, or
// expired) — the server returns 404 for both cases, so a 404 is the only
// signal that means "clear it and go back to idle." Any other error is
// transient and must be preserved/retried, not discarded.
const isTerminalResumeError = (err) => err instanceof APIError && err.status === 404;

export default function IvoriLandingChatPrompt({ sectionRef }) {
  const navigate = useNavigate();
  const typingTimeoutRef = useRef(null);

  // 'idle' | 'loading_catalogue' | 'pending' | 'typing' | 'answered' | 'error'
  const [phase, setPhase] = useState('loading_catalogue');
  const [prompts, setPrompts] = useState([]);
  const [answered, setAnswered] = useState(null); // { promptText, answerText }
  const [pendingPromptText, setPendingPromptText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // What Retry should actually repeat: 'catalogue' | 'resume' | { promptId }
  const [failedOperation, setFailedOperation] = useState(null);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const loadCatalogue = async () => {
    try {
      const catalogue = await landingHandoffAPI.getPromptCatalogue('apex');
      setPrompts(catalogue.prompts || []);
      setPhase('idle');
    } catch (err) {
      setErrorMessage('Could not load prompts. Please try again.');
      setFailedOperation({ type: 'catalogue' });
      setPhase('error');
    }
  };

  const resumeFromCachedToken = async (cachedToken) => {
    try {
      const resumed = await landingHandoffAPI.resumeHandoff(cachedToken);
      setAnswered({ promptText: resumed.prompt_text, answerText: resumed.answer_text });
      setPhase('answered');
    } catch (err) {
      if (isTerminalResumeError(err)) {
        clearAllPendingHandoffState();
        await loadCatalogue();
        return;
      }
      setErrorMessage('Could not restore your session. Please try again.');
      setFailedOperation({ type: 'resume', token: cachedToken });
      setPhase('error');
    }
  };

  const loadCatalogueAndMaybeResume = async () => {
    const cachedToken = sessionStorage.getItem(STORAGE_KEYS.PENDING_HANDOFF_TOKEN);
    if (cachedToken) {
      await resumeFromCachedToken(cachedToken);
      return;
    }
    await loadCatalogue();
  };

  useEffect(() => {
    loadCatalogueAndMaybeResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPrompt = async (promptId) => {
    setPhase('pending');
    setErrorMessage('');
    try {
      const result = await landingHandoffAPI.createHandoff(promptId);

      sessionStorage.setItem(STORAGE_KEYS.PENDING_HANDOFF_TOKEN, result.handoff_token);
      sessionStorage.setItem(STORAGE_KEYS.PENDING_HANDOFF_PROMPT_ID, result.prompt_id);

      setPendingPromptText(result.prompt_text);
      setPhase('typing');
      typingTimeoutRef.current = setTimeout(() => {
        setAnswered({ promptText: result.prompt_text, answerText: result.answer_text });
        setPhase('answered');
      }, TYPING_PAUSE_MS);
    } catch (err) {
      setErrorMessage('Something went wrong. Please try again.');
      setFailedOperation({ type: 'selectPrompt', promptId });
      setPhase('error');
    }
  };

  const handleRetry = () => {
    setErrorMessage('');
    if (!failedOperation) {
      setPhase('idle');
      return;
    }
    if (failedOperation.type === 'catalogue') {
      setPhase('loading_catalogue');
      loadCatalogue();
      return;
    }
    if (failedOperation.type === 'resume') {
      setPhase('loading_catalogue');
      resumeFromCachedToken(failedOperation.token);
      return;
    }
    if (failedOperation.type === 'selectPrompt') {
      selectPrompt(failedOperation.promptId);
      return;
    }
    setPhase('idle');
  };

  const handleCreateAccount = () => {
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_HANDOFF_RETRY_TOKEN);
    navigate('/login?signup=true', { state: { returnTo: '/chat' } });
  };

  const renderComposerArea = () => {
    if (phase === 'answered' && answered) {
      return (
        <div className="ivori-landing-chat-messages">
          <div className="ivori-landing-chat-msg ivori-landing-user">
            <div className="ivori-landing-who">You</div>
            <div className="ivori-landing-bubble">{answered.promptText}</div>
          </div>
          <div className="ivori-landing-chat-msg ivori-landing-assistant">
            <div className="ivori-landing-who">IVORI</div>
            <div className="ivori-landing-bubble">{answered.answerText}</div>
          </div>
        </div>
      );
    }

    if (phase === 'typing') {
      return (
        <div className="ivori-landing-chat-messages">
          <div className="ivori-landing-chat-msg ivori-landing-user">
            <div className="ivori-landing-who">You</div>
            <div className="ivori-landing-bubble">{pendingPromptText}</div>
          </div>
          <div className="ivori-landing-chat-msg ivori-landing-assistant" aria-label="IVORI is thinking">
            <div className="ivori-landing-who">IVORI</div>
            <div className="ivori-landing-chat-typing-dots">
              <span className="ivori-landing-typing-dot" />
              <span className="ivori-landing-typing-dot" />
              <span className="ivori-landing-typing-dot" />
            </div>
          </div>
        </div>
      );
    }

    return <div className="ivori-landing-chat-empty">{EMPTY_STATE_COPY}</div>;
  };

  const renderComposer = () => {
    if (phase === 'answered' && answered) {
      return (
        <div className="ivori-landing-demo-signup-row">
          <span>Want to save this and keep going?</span>
          <a
            className="ivori-landing-demo-signup-link"
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              handleCreateAccount();
            }}
          >
            Create a free account <span className="ivori-landing-link-arrow">→</span>
          </a>
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div className="ivori-landing-demo-signup-row">
          <span>{errorMessage}</span>
          <button type="button" className="ivori-landing-example-chip" onClick={handleRetry}>
            Retry
          </button>
        </div>
      );
    }

    if (phase === 'typing') {
      // The typing indicator already occupies the message area — the chip
      // rail is fully replaced, not just disabled, so nothing implies
      // another prompt could be picked mid-pause.
      return null;
    }

    const disabled = phase === 'pending' || phase === 'loading_catalogue';

    return (
      <>
        <div className="ivori-landing-demo-examples" id="ivori-landing-demo-examples">
          {prompts.map((prompt) => (
            <button
              type="button"
              className="ivori-landing-example-chip"
              key={prompt.id}
              disabled={disabled}
              aria-label={`Ask: ${prompt.prompt_text}`}
              onClick={() => selectPrompt(prompt.id)}
            >
              {prompt.label}
            </button>
          ))}
        </div>
        <div className="ivori-landing-command-hint">{HINT_COPY}</div>
      </>
    );
  };

  return (
    <section className="ivori-landing-demo-section ivori-landing-fade-up" id="try" ref={sectionRef}>
      <div className="ivori-landing-grain" />
      <div className="ivori-landing-demo-inner">
        <div className="ivori-landing-eyebrow">Decisions the Decision World Model has reasoned through</div>
        <h2 className="ivori-landing-demo-title">Give it a decision — a company, a policy, a moment.</h2>

        <div className="ivori-landing-chat-window">
          {renderComposerArea()}
          <div className="ivori-landing-chat-input-wrap">{renderComposer()}</div>
        </div>
      </div>
    </section>
  );
}
